/**
 * Single source of truth for entity colors across every canvas.
 *
 * Priority: the user-assigned `entity.color` wins. When unset, we fall back to
 * a per-type palette so each canvas still renders something distinguishable.
 *
 * Hex values — CSS variables don't work in MapLibre paint expressions, so we
 * keep concrete hex strings here and reuse them everywhere for consistency.
 */

import type { ApiEntity } from "./api";

export const TYPE_HEX: Record<string, string> = {
  armed_group: "#e55a3c",
  municipality: "#3b82f6",
  military_base: "#22b8d4",
  infrastructure: "#f59e0b",
  individual: "#22c55e",
  political_figure: "#a855f7",
  civilian_organization: "#eab308",
  default: "#3b82f6",
};

export function colorForType(type: string): string {
  return TYPE_HEX[type] ?? TYPE_HEX.default;
}

export function colorForEntity(entity: ApiEntity | { type: string; color?: string | null }): string {
  if (entity.color) return entity.color;
  return colorForType(entity.type);
}

/** Color for a node lookup from id when only the partial entity is available. */
export function colorForEntityRef(
  ref: { type?: string; color?: string | null } | undefined,
  fallbackType?: string,
): string {
  if (!ref) return colorForType(fallbackType ?? "default");
  if (ref.color) return ref.color;
  return colorForType(ref.type ?? fallbackType ?? "default");
}

/** A small curated palette for the slide-over swatches. */
export const SWATCHES: string[] = [
  "#3b82f6", // blue
  "#e55a3c", // orange-red
  "#22c55e", // green
  "#a855f7", // purple
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#eab308", // yellow
  "#ec4899", // pink
  "#64748b", // slate
];
